import { Injectable } from '@angular/core';
import { format, getYear } from 'date-fns';
import { Observable } from 'rxjs';

import { APIResponse } from '../interfaces/api.interface';
import {
  Job,
  JobArchive,
  JobCreate,
  JobSearchParams,
  JobUpdate,
} from '../interfaces/job.interface';
import { queryParamString } from '../utils/query-param-string';
import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class JobService {
  private readonly jobIdCharacters = [
    'Z', // Z should be used as the 0 index
    'A',
    'B',
    'C',
    'D',
    'E',
    'F',
    'G',
    'H',
    'J',
    'K',
    'L',
    'M',
    'N',
    'P',
    'Q',
    'R',
    'S',
    'T',
    'U',
    'V',
    'W',
    'X',
    'Y',
  ];

  public constructor(private readonly api: ApiService) {}

  public generateJobId(organisationId: string): string {
    try {
      const day = format(new Date(), 'dd');
      const month =
        this.jobIdCharacters[Number(format(new Date(), 'MM'))];
      const year = getYear(new Date()) % 10; // use modulo operator to get the last digit
      const hour = this.jobIdCharacters[Number(format(new Date(), 'HH'))];
      const minute = format(new Date(), 'mm');
      const second = format(new Date(), 'ss');
      return `${organisationId}${day}${month}${year}${hour}${minute}${second}`;
    } catch (error) {
        console.log('Error: ', error);
        return `${organisationId}${format(new Date(), 'ddMMkkmm')}`;
    }
  }

  public create(organisationId: string, data: JobCreate): Observable<Job> {
    return this.api.post<Job, JobCreate>(
      `organisations/${organisationId}/orders`,
      data,
    );
  }

  public get(organisationId: string, jobId: string): Observable<Job> {
    return this.api.get<Job>(
      `organisations/${organisationId}/orders/${encodeURIComponent(jobId)}`,
    );
  }

  public search(
    organisationId: string,
    params?: JobSearchParams,
  ): Observable<APIResponse<Job[]>> {
    return this.api.get<APIResponse<Job[]>>(
      queryParamString(`organisations/${organisationId}/orders`, params),
    );
  }

  public update(
    organisationId: string,
    jobId: string,
    data: JobUpdate,
  ): Observable<Job> {
    return this.api.put<Job, JobUpdate>(
      `organisations/${organisationId}/orders/${jobId}`,
      data,
    );
  }

  public archive(
    organisationId: string,
    jobId: string,
    data: JobArchive,
  ): Observable<Job> {
    return this.api.put<Job, JobArchive>(
      `organisations/${organisationId}/orders/${jobId}/archive`,
      data,
    );
  }

  public unarchive(organisationId: string, jobId: string): Observable<Job> {
    return this.api.put<Job, undefined>(
      `organisations/${organisationId}/orders/${jobId}/unarchive`,
    );
  }

  public delete(
    organisationId: string,
    jobId: string,
  ): Observable<Job> {
    return this.api.delete(
      `organisations/${organisationId}/orders/${jobId}`,
    );
  }
}
